package com.tregz.tremenz.ui.media

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener

/** Only the deck that owns the screen sits on this layer. */
private const val Z_ACTIVE = "4"

/** The deck buffering the next song, kept transparent below the active one. */
private const val Z_STAGING = "3"

/** The deck that just finished, parked at the back until it becomes the next stage. */
private const val Z_IDLE = "2"

/** 10 steps of 200ms (2 seconds) crossfade to match original youtube.js. */
private const val FADE_STEPS = 10
private const val FADE_STEP_MS = 200

/** How often the decks are polled and re-handshaken. */
private const val POLL_MS = 1000

/** Start the handover this many seconds before the active song ends. */
private const val ENDING_LEAD_SECONDS = 10.0

/**
 * Poll ticks to wait for the staging deck to announce playback before swapping anyway. Must stay
 * comfortably under [ENDING_LEAD_SECONDS] so a forced swap still lands before the outgoing song
 * runs out.
 */
private const val SWAP_TIMEOUT_TICKS = 6

/** The only origin that may receive commands from, or be heard by, the player decks. */
private const val YOUTUBE_ORIGIN = "https://www.youtube.com"

/**
 * State shared between the song-request effect and the crossfade running in the poll effect: a
 * song picked mid-fade is parked here rather than written to the deck that is fading in.
 */
private class DeckGate {
    var isFading = false
    var queuedId: String? = null
}

private fun embedUrl(videoId: String) =
    "https://www.youtube.com/embed/$videoId?enablejsapi=1&autoplay=1&playsinline=1"

@Composable
actual fun MediaEmbedVideo(
    videoId: String,
    isPlaying: Boolean,
    onStateChange: (Int) -> Unit,
    onError: (Int) -> Unit,
    onEnding: () -> Unit,
    onAdDetected: () -> Unit,
    isContinuousMix: Boolean,
    modifier: Modifier
) {
    val instanceId = remember { (1000..9999).random() }
    val deck1Id = "${instanceId}1".toInt()
    val deck2Id = "${instanceId}2".toInt()
    val deck3Id = "${instanceId}3".toInt()

    var bounds by remember { mutableStateOf<Rect?>(null) }

    // Rotating 3-deck system: the active deck owns the screen, the staging deck buffers the next
    // song out of sight, and the idle deck waits at the back to become the next stage.
    var activeDeck by remember { mutableStateOf(1) }
    var stagingDeck by remember { mutableStateOf(2) }
    var idleDeck by remember { mutableStateOf(3) }

    var currentLoadedId by remember { mutableStateOf<String?>(null) }
    var stagedId by remember { mutableStateOf<String?>(null) }

    // True from the moment the next song is requested until the crossfade has rotated the decks.
    // It must NOT be cleared when the new videoId arrives: the outgoing deck is still active and
    // still reporting its final seconds, so clearing it there makes the poll below re-request the
    // next song once per second, restarting the staging iframe before it can ever start playing.
    var isTransitionPending by remember { mutableStateOf(false) }

    // Last player state seen per deck, so infoDelivery repeats don't re-fire transitions.
    val lastState = remember { IntArray(3) { -1 } }

    fun createDeck(id: Int, zIndex: String): HTMLIFrameElement {
        return (document.createElement("iframe") as HTMLIFrameElement).apply {
            this.id = "yt_deck_$id"
            setAttribute("allow", "autoplay; encrypted-media; picture-in-picture")
            setAttribute("allowfullscreen", "true")
            style.position = "fixed"
            style.border = "none"
            style.zIndex = zIndex
            style.visibility = "hidden"
            style.opacity = "1"
        }
    }

    val deck1 = remember { createDeck(deck1Id, Z_ACTIVE) }
    val deck2 = remember { createDeck(deck2Id, Z_STAGING) }
    val deck3 = remember { createDeck(deck3Id, Z_IDLE) }

    fun frameOf(deck: Int): HTMLIFrameElement = when (deck) {
        1 -> deck1
        2 -> deck2
        else -> deck3
    }

    fun deckIdOf(deck: Int): Int = when (deck) {
        1 -> deck1Id
        2 -> deck2Id
        else -> deck3Id
    }

    // Single source of truth for stacking. Assigning all three decks every time keeps two of them
    // from tying on the same layer and falling back to DOM order.
    fun applyStacking() {
        frameOf(activeDeck).style.zIndex = Z_ACTIVE
        frameOf(stagingDeck).style.zIndex = Z_STAGING
        frameOf(idleDeck).style.zIndex = Z_IDLE
    }

    val currentOnStateChange by rememberUpdatedState(onStateChange)
    val currentOnError by rememberUpdatedState(onError)
    val currentOnEnding by rememberUpdatedState(onEnding)
    val currentOnAdDetected by rememberUpdatedState(onAdDetected)
    val currentContinuousMix by rememberUpdatedState(isContinuousMix)
    val currentIsPlaying by rememberUpdatedState(isPlaying)

    val gate = remember { DeckGate() }

    // Stage the song out of sight; the swap happens once the deck reports playback. The gate
    // stays closed until that rotation completes, otherwise the poll re-requests the next song
    // every second and keeps resetting this src.
    fun stageVideo(id: String) {
        isTransitionPending = true
        stagedId = id
        lastState[stagingDeck - 1] = -1

        val staging = frameOf(stagingDeck)
        staging.style.visibility = "hidden"
        staging.style.opacity = "0"
        staging.style.zIndex = Z_STAGING
        staging.src = embedUrl(id)
    }

    DisposableEffect(Unit) {
        document.body?.appendChild(deck1)
        document.body?.appendChild(deck2)
        document.body?.appendChild(deck3)

        var fadeTimer: Int? = null
        var pollTimer: Int? = null
        var swapWaitTicks = 0

        var currentDuration = 0.0
        var currentVideoTime = 0.0

        // The ad heuristic reports once per song, not once per player message.
        var isAdReported = false

        // The YouTube player discards messages that arrive before it has booted, so this is resent
        // on every poll rather than once. The id is what routes the player's replies back to a
        // deck, so it has to be present on every handshake.
        fun handshake(deck: Int) {
            frameOf(deck).contentWindow?.postMessage(
                """{"event":"listening","id":${deckIdOf(deck)}}""", YOUTUBE_ORIGIN
            )
        }

        fun command(deck: Int, func: String, args: String = "\"\"") {
            frameOf(deck).contentWindow?.postMessage(
                """{"event":"command","func":"$func","args":$args}""", YOUTUBE_ORIGIN
            )
        }

        fun beginSwap(incomingDeck: Int) {
            if (fadeTimer != null || incomingDeck == activeDeck) return
            gate.isFading = true

            // Pinned for the whole fade: the timer below must keep addressing the deck it started
            // with, not whatever is active by the time a tick runs.
            val outgoingDeck = activeDeck
            val incomingFrame = frameOf(incomingDeck)
            val outgoingFrame = frameOf(outgoingDeck)
            console.log("Continuous mix swap: deck $outgoingDeck -> deck $incomingDeck")

            // JS drives the animation below; a CSS transition here would race the interval.
            incomingFrame.style.transition = "none"
            outgoingFrame.style.transition = "none"

            // Hard visual cut: the incoming deck takes the top layer in the same frame the
            // outgoing deck leaves it, so the two are never both visible.
            incomingFrame.style.zIndex = Z_ACTIVE
            incomingFrame.style.visibility = "visible"
            incomingFrame.style.opacity = "1"

            outgoingFrame.style.zIndex = Z_STAGING
            outgoingFrame.style.opacity = "0"

            command(incomingDeck, "playVideo")

            // The incoming deck is what the listener hears now. Without this the app never leaves
            // its "preparing" state and keeps the poster artwork drawn over the player.
            currentOnStateChange(1)

            var fadeStep = 0
            fadeTimer = window.setInterval({
                fadeStep += 1
                val volumeIn = (fadeStep * 100 / FADE_STEPS).coerceAtMost(100)
                val volumeOut = (100 - volumeIn).coerceAtLeast(0)
                command(incomingDeck, "setVolume", "[$volumeIn]")
                command(outgoingDeck, "setVolume", "[$volumeOut]")

                if (fadeStep >= FADE_STEPS) {
                    fadeTimer?.let { window.clearInterval(it) }
                    fadeTimer = null

                    command(outgoingDeck, "stopVideo")
                    outgoingFrame.style.visibility = "hidden"
                    outgoingFrame.style.opacity = "0"

                    // Rotate: the deck that took over is active, the idle deck becomes the next
                    // stage, and the outgoing deck drops to the back.
                    activeDeck = incomingDeck
                    stagingDeck = idleDeck
                    idleDeck = outgoingDeck
                    lastState[outgoingDeck - 1] = -1
                    applyStacking()

                    // Stale position from the outgoing song would otherwise look like "10 seconds
                    // left" and immediately re-arm the trigger for the song that just started.
                    currentDuration = 0.0
                    currentVideoTime = 0.0
                    stagedId = null
                    swapWaitTicks = 0
                    isTransitionPending = false
                    isAdReported = false
                    gate.isFading = false

                    // A song picked during the fade goes to the new stage only now.
                    gate.queuedId?.let { queued ->
                        gate.queuedId = null
                        stageVideo(queued)
                    }
                }
            }, FADE_STEP_MS)
        }

        fun handleState(deck: Int, state: Int) {
            if (lastState[deck - 1] == state) return
            lastState[deck - 1] = state

            if (deck == stagingDeck) {
                // The staged song is rolling: take the top layer now.
                if (state == 1) beginSwap(deck)
                return
            }
            if (deck != activeDeck) return

            currentOnStateChange(state)
            if (state == 1) {
                val frame = frameOf(activeDeck)
                frame.style.visibility = "visible"
                frame.style.opacity = "1"
            }
            // A natural end is advanced upstream by onStateChange(0); calling onEnding as well
            // would advance the queue twice. Arm the gate so the poll stays quiet meanwhile.
            if (state == 0) isTransitionPending = true
        }

        val createListener = { deck: Int ->
            EventListener {
                handshake(deck)
                command(deck, "playVideo")
                // Silence the stage until the crossfade ramps it up.
                if (deck == stagingDeck) command(deck, "setVolume", "[0]")
            }
        }

        val loadListener1 = createListener(1)
        val loadListener2 = createListener(2)
        val loadListener3 = createListener(3)

        deck1.addEventListener("load", loadListener1)
        deck2.addEventListener("load", loadListener2)
        deck3.addEventListener("load", loadListener3)

        val messageListener = object : EventListener {
            override fun handleEvent(event: Event) {
                // Any frame on the page can post a message; only the player decks are heard.
                val source: dynamic = event.asDynamic().source
                val isDeck = source === deck1.contentWindow || source === deck2.contentWindow ||
                    source === deck3.contentWindow
                if (!isDeck || event.asDynamic().origin != YOUTUBE_ORIGIN) return

                val dataAny: dynamic = event.asDynamic().data
                if (dataAny != null && js("typeof dataAny") == "string") {
                    // Unparseable frames are normal chatter; real bugs below must still surface.
                    val parsed: dynamic = try {
                        JSON.parse<dynamic>(dataAny)
                    } catch (_: Throwable) {
                        return
                    }
                    run {
                        val id = parsed.id as? Int

                        val eventDeck = when (id) {
                            deck1Id -> 1
                            deck2Id -> 2
                            deck3Id -> 3
                            else -> 0
                        }
                        if (eventDeck == 0) return

                        when (parsed.event as? String) {
                            "onStateChange" -> {
                                val state = parsed.info as? Int
                                if (state != null) handleState(eventDeck, state)
                            }

                            "infoDelivery" -> {
                                val info = parsed.info
                                if (info != null) {
                                    // Second, far more frequent source of truth for playback:
                                    // onStateChange alone is lost whenever a handshake was missed.
                                    val playerState = info.playerState as? Int
                                    if (playerState != null) handleState(eventDeck, playerState)

                                    if (eventDeck == activeDeck) {
                                        val duration = (info.duration as? Number)?.toDouble() ?: 0.0
                                        val time = (info.currentTime as? Number)?.toDouble() ?: 0.0
                                        if (duration > 0.0) currentDuration = duration
                                        if (time > 0.0) currentVideoTime = time

                                        if (!isAdReported && duration > 0.0 && duration <= 35.0) {
                                            isAdReported = true
                                            currentOnAdDetected()
                                        }
                                    }
                                }
                            }

                            "onError" -> {
                                if (eventDeck == activeDeck) {
                                    val code = parsed.info as? Int
                                    if (code != null) currentOnError(code)
                                }
                            }
                        }
                    }
                }
            }
        }
        window.addEventListener("message", messageListener)

        fun poll() {
            // Re-register every deck. A staging deck that never registers never reports the
            // playing state that drives the swap, which leaves the outgoing video stuck on top.
            handshake(activeDeck)
            handshake(stagingDeck)
            handshake(idleDeck)

            // Keep the stage silent while it buffers. The mute sent on iframe load races the
            // player's boot exactly like the handshake does, so it is reasserted here until the
            // crossfade takes over the volume ramp.
            if (fadeTimer == null) command(stagingDeck, "setVolume", "[0]")

            // A play or pause pressed while the iframe was still booting is lost; keep asserting
            // it until the deck has reported a state of its own.
            if (lastState[activeDeck - 1] == -1) {
                command(activeDeck, if (currentIsPlaying) "playVideo" else "pauseVideo")
            }

            if (isTransitionPending) {
                swapWaitTicks += 1
                if (fadeTimer == null && swapWaitTicks >= SWAP_TIMEOUT_TICKS) {
                    swapWaitTicks = 0
                    val staged = stagedId
                    if (staged == null) {
                        // Nothing was ever staged (continuous mix off, or the request was
                        // dropped). Release the gate so a later song can still transition.
                        isTransitionPending = false
                    } else {
                        // It is autoplaying, we simply never heard about it. Swapping beats
                        // leaving the previous video on screen over the new audio.
                        console.warn("Staging deck silent for $staged; forcing swap (web)")
                        beginSwap(stagingDeck)
                    }
                }
            } else if (currentContinuousMix) {
                swapWaitTicks = 0

                val remaining = currentDuration - currentVideoTime
                if (currentDuration > ENDING_LEAD_SECONDS && currentVideoTime > 0.0 &&
                    remaining < ENDING_LEAD_SECONDS
                ) {
                    isTransitionPending = true
                    console.log(
                        "Continuous mix transition requested (web): " +
                            "$currentVideoTime / $currentDuration"
                    )
                    currentOnEnding()
                }
            }
        }

        pollTimer = window.setInterval({ poll() }, POLL_MS)

        // Hidden tabs throttle timers and frames to a crawl; catch up the moment the tab returns.
        val visibilityListener = EventListener {
            if (document.asDynamic().visibilityState == "visible") poll()
        }
        document.addEventListener("visibilitychange", visibilityListener)

        onDispose {
            document.removeEventListener("visibilitychange", visibilityListener)
            fadeTimer?.let { window.clearInterval(it) }
            window.clearInterval(pollTimer)
            deck1.removeEventListener("load", loadListener1)
            deck2.removeEventListener("load", loadListener2)
            deck3.removeEventListener("load", loadListener3)
            window.removeEventListener("message", messageListener)
            deck1.remove()
            deck2.remove()
            deck3.remove()
        }
    }

    LaunchedEffect(videoId) {
        val isFirstSong = (currentLoadedId == null)
        currentLoadedId = videoId

        if (isFirstSong) {
            lastState[activeDeck - 1] = -1
            val active = frameOf(activeDeck)
            active.src = embedUrl(videoId)
            active.style.visibility = "visible"
            active.style.opacity = "1"
            applyStacking()
        } else {
            // While a fade runs the staging deck is the one fading in, so the pick waits for it.
            if (gate.isFading) gate.queuedId = videoId else stageVideo(videoId)
        }
    }

    val overlayTop = LocalPlayerOverlayTop.current

    val viewport = rememberMediaViewport()

    LaunchedEffect(bounds, overlayTop, viewport) {
        val b = bounds ?: return@LaunchedEffect
        val dpr = window.devicePixelRatio.takeIf { it > 0 } ?: 1.0
        val left = "${(b.left / dpr).toInt()}px"
        val topPx = (b.top / dpr).toInt()
        val heightPx = (b.height / dpr).toInt()
        val top = "${topPx}px"
        val width = "${(b.width / dpr).toInt()}px"
        val height = "${heightPx}px"

        val decks = listOf(deck1, deck2, deck3)
        decks.forEach { deck ->
            deck.style.left = left
            deck.style.top = top
            deck.style.width = width
            deck.style.height = height
        }

        if (overlayTop != null) {
            val coveredPx = heightPx - overlayTop.value
            decks.forEach { deck ->
                if (coveredPx >= heightPx - 1) {
                    deck.style.visibility = "hidden"
                    deck.style.setProperty("pointer-events", "none")
                    deck.style.setProperty("clip-path", "inset(0 0 100% 0)")
                } else if (coveredPx > 0) {
                    if (deck == frameOf(activeDeck)) {
                        deck.style.visibility = "visible"
                    }
                    deck.style.setProperty("pointer-events", "none")
                    deck.style.setProperty("clip-path", "inset(0 0 ${coveredPx.toInt()}px 0)")
                } else {
                    if (deck == frameOf(activeDeck)) {
                        deck.style.visibility = "visible"
                    }
                    deck.style.setProperty("pointer-events", "auto")
                    deck.style.setProperty("clip-path", "none")
                }
            }
        } else {
            decks.forEach { deck ->
                deck.style.setProperty("pointer-events", "auto")
                deck.style.setProperty("clip-path", "none")
            }
        }
    }

    LaunchedEffect(isPlaying) {
        val cmd = if (isPlaying) "playVideo" else "pauseVideo"
        frameOf(activeDeck).contentWindow?.postMessage(
            """{"event":"command","func":"$cmd","args":""}""", YOUTUBE_ORIGIN
        )
    }

    Box(
        modifier = modifier
            .background(Color.Black)
            .onGloballyPositioned { coordinates ->
                bounds = coordinates.boundsInWindow()
            }
    )
}

private fun audioEmbedUrl(url: String?): String? {
    if (url.isNullOrBlank()) return null
    return when {
        url.contains("soundcloud.com") -> {
            val encoded = js("encodeURIComponent(url)") as String
            "https://w.soundcloud.com/player/?url=$encoded&color=%23ff5500&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true"
        }
        url.contains("mixcloud.com") -> {
            val path = url.removePrefix("https://www.mixcloud.com").removePrefix("http://www.mixcloud.com")
            val feed = js("encodeURIComponent(path)") as String
            "https://player-widget.mixcloud.com/widget/iframe/?light=1&autoplay=1&feed=$feed"
        }
        else -> null
    }
}

@Composable
actual fun MediaEmbedAudio(url: String?, modifier: Modifier) {
    var bounds by remember { mutableStateOf<Rect?>(null) }
    val overlayTop = LocalPlayerOverlayTop.current
    val viewport = rememberMediaViewport()

    val iframe = remember {
        (document.createElement("iframe") as HTMLIFrameElement).apply {
            id = "audio_embed_player"
            setAttribute("allow", "autoplay; encrypted-media")
            setAttribute("allowfullscreen", "true")
            style.position = "fixed"
            style.border = "none"
            style.zIndex = Z_ACTIVE
            style.visibility = "hidden"
        }
    }

    DisposableEffect(Unit) {
        document.body?.appendChild(iframe)
        onDispose {
            iframe.remove()
        }
    }

    val embed = remember(url) { audioEmbedUrl(url) }

    LaunchedEffect(embed) {
        if (embed != null) {
            iframe.src = embed
            iframe.style.visibility = "visible"
        } else {
            iframe.src = "about:blank"
            iframe.style.visibility = "hidden"
        }
    }

    LaunchedEffect(bounds, overlayTop, viewport) {
        val b = bounds ?: return@LaunchedEffect
        val dpr = window.devicePixelRatio.takeIf { it > 0 } ?: 1.0
        val topPx = (b.top / dpr).toInt()
        val heightPx = (b.height / dpr).toInt()

        iframe.style.left = "${(b.left / dpr).toInt()}px"
        iframe.style.top = "${topPx}px"
        iframe.style.width = "${(b.width / dpr).toInt()}px"
        iframe.style.height = "${heightPx}px"

        if (overlayTop != null) {
            val coveredPx = heightPx - overlayTop.value
            if (coveredPx >= heightPx - 1) {
                iframe.style.visibility = "hidden"
                iframe.style.setProperty("pointer-events", "none")
                iframe.style.setProperty("clip-path", "inset(0 0 100% 0)")
            } else if (coveredPx > 0) {
                iframe.style.visibility = "visible"
                iframe.style.setProperty("pointer-events", "none")
                iframe.style.setProperty("clip-path", "inset(0 0 ${coveredPx.toInt()}px 0)")
            } else {
                iframe.style.visibility = "visible"
                iframe.style.setProperty("pointer-events", "auto")
                iframe.style.setProperty("clip-path", "none")
            }
        } else {
            iframe.style.visibility = "visible"
            iframe.style.setProperty("pointer-events", "auto")
            iframe.style.setProperty("clip-path", "none")
        }
    }

    Box(
        modifier = modifier
            .background(Color.Black)
            .onGloballyPositioned { coordinates ->
                bounds = coordinates.boundsInWindow()
            }
    )
}
